import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { JobResponseSchema } from '@/api/client';
import { DataTable } from './DataTable';
import { ErrorModal } from './ErrorModal';

interface Props {
  jobs: JobResponseSchema[];
  isLoading: boolean;
  isRefetching?: boolean;
  onRestartJob: (jobId: string) => void;
}

function useScrollPinToBottom(deps: unknown[]) {
  const isPinnedRef = useRef(false);

  const checkIfAtBottom = useCallback(() => {
    const threshold = 50;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      isPinnedRef.current = checkIfAtBottom();
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [checkIfAtBottom]);

  useEffect(() => {
    if (isPinnedRef.current) {
      window.scrollTo({ top: document.documentElement.scrollHeight });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const columnHelper = createColumnHelper<JobResponseSchema>();

const STATUS_BADGE_MAP: Record<string, string> = {
  completed: 'admin-badge-success',
  failed: 'admin-badge-error',
  running: 'admin-badge-info',
  pending: 'admin-badge-warning',
};

export function JobsView({ jobs, isLoading, isRefetching, onRestartJob }: Props) {
  useScrollPinToBottom([jobs]);
  const [selectedError, setSelectedError] = useState<string | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => (
          <span className="admin-mono" title={info.getValue()}>
            {info.getValue().slice(0, 8)}...
          </span>
        ),
      }),
      columnHelper.accessor('job_type', {
        header: 'Type',
        cell: (info) => (
          <span className="admin-badge admin-badge-neutral">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('resource_type', {
        header: 'Resource',
        cell: (info) => (
          <span>
            {info.getValue()}{' '}
            <span className="admin-mono">#{info.row.original.resource_id}</span>
          </span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          const badgeClass = STATUS_BADGE_MAP[status] || 'admin-badge-neutral';
          return (
            <span className={`admin-badge ${badgeClass}`}>
              {status}
            </span>
          );
        },
      }),
      columnHelper.accessor('attempts', {
        header: 'Attempts',
        cell: (info) => (
          <span className="admin-mono">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => (
          <span className="admin-mono" style={{ fontSize: '11px' }}>
            {new Date(info.getValue()).toLocaleString()}
          </span>
        ),
      }),
      columnHelper.accessor('started_at', {
        header: 'Started',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>;
          return (
            <span className="admin-mono" style={{ fontSize: '11px' }}>
              {new Date(val).toLocaleString()}
            </span>
          );
        },
      }),
      columnHelper.accessor('completed_at', {
        header: 'Completed',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>;
          return (
            <span className="admin-mono" style={{ fontSize: '11px' }}>
              {new Date(val).toLocaleString()}
            </span>
          );
        },
      }),
      columnHelper.accessor('last_error', {
        header: 'Error',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>;
          return (
            <button
              className="admin-error-link"
              onClick={() => setSelectedError(val)}
            >
              {val.slice(0, 40)}...
            </button>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => {
          const { status, last_error } = info.row.original;
          const restartable = status === 'failed' || (status === 'pending' && last_error);
          if (!restartable) return null;
          return (
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => onRestartJob(info.row.original.id)}
            >
              Restart
            </button>
          );
        },
      }),
    ],
    [onRestartJob]
  );

  return (
    <>
      <DataTable
        data={jobs}
        columns={columns}
        isLoading={isLoading}
        isRefetching={isRefetching}
        loadingMessage="Loading jobs..."
      />
      {selectedError && (
        <ErrorModal
          error={selectedError}
          onClose={() => setSelectedError(null)}
        />
      )}
    </>
  );
}
